import { type FC } from "react";

import { ResponseType, useResponsesForPage } from "./responses";

import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { NewSubmissionBadge } from "components/materials/page/assessment/NewSubmissionBadge";
import { getOfficialSubmission } from "components/materials/page/helpers";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import { CircleExclamationIcon, PersonGroupIcon, PersonSingleIcon, VerifiedIcon } from "mds/icons";
import { pageSelectors, selectSortedSubmissionsByAccessId } from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { isGroupPage } from "utils/page-utils";
import { useQueryParamsActions } from "utils/routing";

const useSortedSubmissionsByAccessId = (pageId: string, accessId: string) =>
  useListSelector(
    (s) => selectSortedSubmissionsByAccessId(s, pageId, accessId),
    ["created_at", "seen_after_assessed_at", "official_at"],
  );

interface StudentDropdownItemIconParams {
  pageId: string;
  accessId: string;
  isGroup: boolean;
}

const StudentDropdownItemIcon: FC<StudentDropdownItemIconParams> = ({
  pageId,
  accessId,
  isGroup,
}) => {
  const submissions = useSortedSubmissionsByAccessId(pageId, accessId);

  if (submissions.length === 0) {
    return (
      <div className="text-orange">
        <CircleExclamationIcon />
      </div>
    );
  }

  if (getOfficialSubmission(submissions)) {
    return <VerifiedIcon className="text-green" />;
  }

  return isGroup ? <PersonGroupIcon /> : <PersonSingleIcon />;
};

const StudentDropdownItemNewBadge: FC<{ pageId: string; accessId: string }> = ({
  pageId,
  accessId,
}) => {
  const submissions = useSortedSubmissionsByAccessId(pageId, accessId);
  return <NewSubmissionBadge sortedSubmissions={submissions} />;
};

interface StudentDropdownProps {
  pageId: string;
}

export const StudentDropdown: FC<StudentDropdownProps> = ({ pageId }) => {
  const { setQueryParams, deleteQueryParams } = useQueryParamsActions();
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);
  const { responses } = useResponsesForPage(pageId);

  const isAssessing = useIsAssessing();
  const isLargeScreen = useIsLgOrLarger();
  const selectedResponse = useFindItemBySearchParamId(responses, ACCESS_ID_QUERY_PARAM, true);

  if (!page || !selectedResponse || (isAssessing && isLargeScreen)) {
    return null;
  }

  const isGroup = isGroupPage(page);

  const onSelectionChange = (item: ResponseType) => {
    if (item.id) {
      setQueryParams({ [ACCESS_ID_QUERY_PARAM]: item.id, [SUBMISSION_ID_QUERY_PARAM]: null });
    } else {
      deleteQueryParams([ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM]);
    }
  };

  const items: ListboxOption[] = responses.map((response) => ({
    id: response.id,
    title: response.title,
    iconEnd: <StudentDropdownItemNewBadge accessId={response.id} pageId={pageId} />,
    iconStart: <StudentDropdownItemIcon accessId={response.id} isGroup={isGroup} pageId={pageId} />,
  }));

  return (
    <Listbox
      buttonSize="s"
      items={items}
      itemSize="xs"
      selectedValue={selectedResponse.id}
      onSelectionChange={onSelectionChange}
    />
  );
};
